import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { startOfMonth, endOfMonth, subMonths } from 'date-fns';

interface FinancialOverview {
  currentMonthRevenue: number;
  previousMonthRevenue: number;
  percentChange: number;
  outstandingPayments: number;
  overduePayments: number;
  avgRevenuePerStudent: number;
  topOutstandingInvoices: Array<{
    id: string;
    familyName: string;
    amount: number;
    dueIn: number;
  }>;
}

export const useFinancialOverview = () => {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;

  return useQuery({
    queryKey: ['financialOverview', organizationId],
    queryFn: async (): Promise<FinancialOverview> => {
      const now = new Date();
      const currentMonthStart = startOfMonth(now);
      const currentMonthEnd = endOfMonth(now);
      const lastMonthStart = startOfMonth(subMonths(now, 1));
      const lastMonthEnd = endOfMonth(subMonths(now, 1));

      const [currentMonthPayments, previousMonthPayments, unpaidInvoices, students] = await Promise.all([
        supabase
          .from('payments')
          .select('amount_cents')
          .eq('organization_id', organizationId)
          .gte('paid_at', currentMonthStart.toISOString())
          .lte('paid_at', currentMonthEnd.toISOString()),

        supabase
          .from('payments')
          .select('amount_cents')
          .eq('organization_id', organizationId)
          .gte('paid_at', lastMonthStart.toISOString())
          .lte('paid_at', lastMonthEnd.toISOString()),

        supabase
          .from('invoices')
          .select(`
            id,
            total_cents,
            due_date,
            families (name)
          `)
          .eq('organization_id', organizationId)
          .eq('status', 'unpaid')
          .order('due_date', { ascending: true }),

        supabase
          .from('students')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
      ]);

      const currentRevenue = (currentMonthPayments.data || [])
        .reduce((sum, p) => sum + (p.amount_cents || 0), 0);
      const previousRevenue = (previousMonthPayments.data || [])
        .reduce((sum, p) => sum + (p.amount_cents || 0), 0);
      
      const totalOutstanding = (unpaidInvoices.data || [])
        .reduce((sum, inv) => sum + (inv.total_cents || 0), 0);
      
      const overdueAmount = (unpaidInvoices.data || [])
        .filter(inv => new Date(inv.due_date) < now)
        .reduce((sum, inv) => sum + (inv.total_cents || 0), 0);

      const studentCount = students.count || 0;
      const avgRevenue = studentCount > 0 ? currentRevenue / studentCount : 0;

      const topOutstandingInvoices = (unpaidInvoices.data || [])
        .slice(0, 3)
        .map(inv => ({
          id: inv.id,
          familyName: inv.families?.name || 'Unknown Family',
          amount: inv.total_cents / 100,
          dueIn: Math.ceil((new Date(inv.due_date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
        }));

      return {
        currentMonthRevenue: currentRevenue,
        previousMonthRevenue: previousRevenue,
        percentChange: previousRevenue > 0 ? 
          ((currentRevenue - previousRevenue) / previousRevenue) * 100 : 0,
        outstandingPayments: totalOutstanding,
        overduePayments: overdueAmount,
        avgRevenuePerStudent: avgRevenue,
        topOutstandingInvoices
      };
    },
    enabled: !!organizationId
  });
};
